export default class ProductSwatchList extends HTMLElement {
  constructor() {
    super();
    this._swatches = [];
    this._orderedSwatches = [];
    this._currentStyle = '';
    this._templateReady = false;
    this._clubbedProducts = null;
  }

  get swatches() {
    return this._swatches;
  }

  set swatches(val) {
    this._swatches = val;
    if (this._templateReady) this.renderSwatches();
  }

  get orderedSwatches() {
    return this._orderedSwatches;
  }

  set orderedSwatches(val) {
    this._orderedSwatches = val;
    if (this._templateReady) this.renderSwatches();
  }

  get currentStyle() {
    return this._currentStyle;
  }

  set currentStyle(val) {
    this._currentStyle = val;
    if (this._templateReady) this.renderSwatches();
  }

  get clubbedProducts() {
    return this._clubbedProducts;
  }

  set clubbedProducts(val) {
    this._clubbedProducts = val;
    if (this._templateReady) this.renderSwatches();
  }

  get shop_url() {
    return this._shop_url;
  }

  connectedCallback() {
    this.renderTemplate();
    this.initEventListeners();
  }

  renderTemplate() {
    const template = document.createElement('template');
    template.innerHTML = `
      <div class="dropdown-menu fixed-block out-of-sight" id="swatchList">
        <div class="swatch-list-wrapper">
          <div class="container-fluid">
            <div class="row">
              <div class="col-md-10 col-md-offset-1 col-xs-12 col-xs-offset-0">
                <div class="header">
                  <a class="title-wrapper" href="#" id="closeSwatchListBtn">
                    <div class="button button-medium rounded" id=""></div>
                    <p class="title">All Colours</p>
                  </a>
                </div>
                <nav class="swatch-list">
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
    this.appendChild(template.content.cloneNode(true));
    this._templateReady = true;
    this.renderSwatches();
    this.setInitialSelection();
  }

  renderSwatches() {
    
    const swatchList = this.querySelector('.swatch-list');
    if (!swatchList) return;

    swatchList.innerHTML = '';
    
    // Check if we have ordered swatches from the parent
    if (this.orderedSwatches && this.orderedSwatches.length > 0) {
      
      // Simply render the swatches in the order they were provided
      this.orderedSwatches.forEach(swatch => {
        // Check if this swatch should be shown for current style
        if (swatch.style === this.currentStyle) {
          const swatchLink = this.createSwatchLink(swatch, swatch.is_clubbed);
          swatchList.appendChild(swatchLink);
        }
      });
      
      return;
    }
        
    // Create fragments for sale and non-sale products
    const saleProductsFragment = document.createDocumentFragment();
    const nonSaleProductsFragment = document.createDocumentFragment();

    // Sort main swatches into sale and non-sale
    const sortedSwatches = Array.from(this.swatches).sort((a, b) => {
      if (a.is_sale_product && !b.is_sale_product) return -1;
      if (!a.is_sale_product && b.is_sale_product) return 1;
      return 0;
    });

    // Process main swatches
    sortedSwatches.forEach(swatch => {
      if (swatch.style === this.currentStyle) {
        const swatchLink = this.createSwatchLink(swatch);
        if (swatch.is_sale_product) {
          saleProductsFragment.appendChild(swatchLink);
        } else {
          nonSaleProductsFragment.appendChild(swatchLink);
        }
      }
    });

    // Add all fragments to the swatch list
    swatchList.appendChild(saleProductsFragment);
    swatchList.appendChild(nonSaleProductsFragment);
  }

  // Helper method to create a swatch link element
  createSwatchLink(swatch, isClubbed = false) {
    const swatchLink = document.createElement('a');
    swatchLink.href = "#";
    swatchLink.className = 'swatch-link';
    
    // Add class if this swatch is already displayed in nav
    if (swatch.is_displayed_in_nav) {
      swatchLink.classList.add('displayed-in-nav');
    }
    
    // Set data attributes
    swatchLink.setAttribute('data-group', swatch.group);
    swatchLink.setAttribute('data-urlSlug', swatch.url_slug);
    swatchLink.setAttribute('data-url', swatch.shop_url);
    swatchLink.setAttribute('data-style', swatch.style);
    swatchLink.setAttribute('data-type', swatch.type);
    swatchLink.setAttribute('data-variant', swatch.variant);
    
    if (isClubbed) {
      swatchLink.setAttribute('data-clubbed', 'true');
    }

    // Create HTML content
    swatchLink.innerHTML = `
      <div class="thumbnail-wrapper">
        <div class="thumbnail">
          <img src="${swatch.hero_shop_image}"/>
        </div>
      </div>
      <div class="info">
        <p class="footnote colour-name">${swatch.name}</p>
        ${isClubbed ? `<p class="footnote category-name">${swatch.type_name}</p>` : ''}
        ${swatch.is_sale_product == true ? `<p class="footnote savings">Clearance</p>` : ''}
      </div>
    `;

    return swatchLink;
  }

  initEventListeners() {
    const swatchList = this.querySelector('#swatchList');
    const closeBtn = this.querySelector('#closeSwatchListBtn');

    if (closeBtn) {
      closeBtn.addEventListener('click', (event) => {
        event.preventDefault();
        swatchList.classList.add('out-of-sight');
      });
    }

    // Add event listeners after rendering
    this.addSwatchLinkEventListeners();
  }

  // Helper method to determine event name based on link type
  getEventNameForLink(link) {
    const isClubbed = link.hasAttribute('data-clubbed') && link.getAttribute('data-clubbed') === 'true';
    return isClubbed ? 'ClubbedProductClick' : 'SwatchListClick';
  }

  // Helper method to dispatch swatch event
  dispatchSwatchEvent(link, event, storeState = true) {
    const eventName = this.getEventNameForLink(link);
    
    const clickEvent = new CustomEvent(eventName, {
      detail: {
        link: link,
        e: event,
        storeState: storeState
      }
    });
    
    const productNav = document.querySelector('product-nav');
    if (productNav) {
      productNav.dispatchEvent(clickEvent);
    }
  }

  // Helper method to handle swatch selection (common logic)
  selectSwatch(link, event = null, shouldDispatchEvent = true) {
    link.classList.add('selected');
    
    if (shouldDispatchEvent) {
      this.dispatchSwatchEvent(link, event);
    }
  }

  // Helper method to hide swatch list
  hideSwatchList() {
    const swatchList = this.querySelector('#swatchList');
    if (swatchList) {
      swatchList.classList.add('out-of-sight');
    }
  }

  addSwatchLinkEventListeners() {
    const swatchList = this.querySelector('#swatchList');
    const swatchLinks = swatchList.querySelectorAll('.swatch-link');

    swatchLinks.forEach(link => {
      // Avoid adding duplicate event listeners
      if (link.hasAttribute('has-listener')) return;
      
      link.setAttribute('has-listener', 'true');
      link.addEventListener('click', (event) => {
        event.preventDefault();
        
        // Use the modular selection method
        this.selectSwatch(link, event);
        this.hideSwatchList();
      });
    });
  }

  setInitialSelection() {
    if (typeof(shop_url) !== 'undefined' && shop_url) {

      const swatchLinks = this.querySelectorAll('.swatch-link');
      for (let link of swatchLinks) {
        const dataUrl = link.getAttribute('data-url');
        
        if (dataUrl === shop_url.url) {
          
          // Use the modular selection method
          this.selectSwatch(link, null, true);
          break;
        }
      }
    }
  }
}

window.customElements.define('product-swatch-list', ProductSwatchList);
